import { useCallback, useEffect, useRef, useState } from 'react';
import { worklogApi } from '../../api/worklogApi';
import type { WorklogTimelineItem } from '../../types/worklog.types';

/** 무한스크롤 페이지 크기 — 하단 도달 시 다음 페이지를 append. */
const PAGE_SIZE = 20;

export type FeedLoadState = 'loading' | 'ready' | 'error';

/**
 * 공사현황 통합 타임라인(§1.5) 조회 + 무한스크롤 — 공사현황 전체 화면과 나의 현장 '공사현황' 탭 공용.
 *
 * 통합 타임라인 단일 API가 작업요청+현장보고를 서버에서 병합·페이징한다. 기간(dateFrom~dateTo)과 선택적 현장(siteId)만
 * 서버 조건이고, 반장·정렬은 로드된 피드에 화면이 적용한다.
 * `enabled`가 false인 동안은 조회하지 않는다(기준정보·현장목록 로딩 게이트).
 */
export function useWorklogFeed({
  dateFrom, dateTo, siteId, enabled = true,
}: {
  dateFrom: string;
  dateTo: string;
  /** 지정하면 그 현장의 작업요청·현장보고만 서버가 걸러 준다(단일 현장 피드). 생략=사업장 전체 */
  siteId?: number;
  enabled?: boolean;
}) {
  const [feedItems, setFeedItems] = useState<WorklogTimelineItem[]>([]);
  const [state, setState] = useState<FeedLoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // 무한스크롤 — 단일 커서(offset). 다음 페이지·추가여부는 ref로 관리(observer 콜백의 stale 클로저 방지)
  const pageRef = useRef(0);
  const hasNextRef = useRef(false);
  const loadingMoreRef = useRef(false);
  /**
   * 조회 세대 — 0페이지 조회(조건 변경·재조회)가 시작될 때마다 올라간다. 진행 중이던 응답이 새 조회보다 늦게 도착해도
   * 세대가 다르면 버려, 이전 조건의 항목이 새 목록 뒤에 붙거나 최신 응답을 덮어쓰지 않게 한다.
   */
  const genRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // 초기(또는 조건변경) 조회 — 커서 리셋 + 0페이지 조회(교체).
  const fetchFeed = useCallback(
    (signal?: AbortSignal) => {
      const gen = ++genRef.current;
      // 이전 조건의 추가 조회는 무효가 됐으니 진행 표시도 풀어, 새 목록에서 바로 다음 페이지를 받을 수 있게 한다
      loadingMoreRef.current = false;
      setLoadingMore(false);
      return worklogApi
        .getTimeline({ dateFrom, dateTo, siteId, page: 0, size: PAGE_SIZE }, signal)
        .then((res) => {
          if (gen !== genRef.current) return;   // 더 새로운 조회가 시작됨 — 이 응답은 버린다
          const d = res.data.data;
          setFeedItems(d.items);
          pageRef.current = 0;
          hasNextRef.current = d.hasNext;
          setHasMore(d.hasNext);
          setState('ready');
        });
    },
    [dateFrom, dateTo, siteId],
  );

  // 추가 조회(무한스크롤) — 단일 커서 다음 페이지 조회 후 append.
  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasNextRef.current) return;
    const gen = genRef.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    void worklogApi
      .getTimeline({ dateFrom, dateTo, siteId, page: nextPage, size: PAGE_SIZE })
      .then((res) => {
        if (gen !== genRef.current) return;   // 조건이 바뀌었거나 재조회됨 — 이전 조건의 페이지는 붙이지 않는다
        const d = res.data.data;
        setFeedItems((prev) => [...prev, ...d.items]);
        hasNextRef.current = d.hasNext;
        pageRef.current = nextPage;
      })
      .catch(() => {/* 추가 조회 실패는 조용히 무시(기존 목록 유지) */})
      .finally(() => {
        if (gen !== genRef.current) return;   // 진행 표시는 새 조회가 이미 정리했다 — 새 추가 조회의 표시를 건드리지 않는다
        setHasMore(hasNextRef.current);
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [dateFrom, dateTo, siteId]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setState('loading');
    const request = fetchFeed(controller.signal);
    const gen = genRef.current;   // fetchFeed 가 방금 올린 세대
    request.catch((err) => {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      if (gen !== genRef.current) return;   // 이미 더 새로운 조회가 대신함 — 늦은 실패로 화면을 오류로 바꾸지 않는다
      setState('error');
    });
    return () => controller.abort();
  }, [enabled, fetchFeed]);

  // 무한스크롤 — 하단 센티넬이 스크롤 뷰에 들어오면 다음 페이지 조회.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || state !== 'ready' || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [state, hasMore, loadMore]);

  /** 재조회 — 즉시 로딩 표시로 전환(클릭 피드백) 후 0페이지부터 다시 조회. 실패 시 error. */
  const reload = useCallback(() => {
    setState('loading');
    const request = fetchFeed();
    const gen = genRef.current;
    request.catch(() => {
      if (gen === genRef.current) setState('error');   // 그 사이 더 새로운 조회가 시작됐으면 이 실패는 무시
    });
  }, [fetchFeed]);

  return { feedItems, state, hasMore, loadingMore, reload, scrollRef, sentinelRef };
}
